import React, { useState, useEffect } from 'react';

const API_BASE = 'https://apispeccheck.zonetap.com';
const LICENSE_REQUEST_LAMBDA = 'https://pngu4f2fmcjwp26q6ytesgy7ti0alvst.lambda-url.us-east-2.on.aws/';

// Access is enforced entirely server-side by /api/billing, which checks
// the user's role on ops.aecworks.ai (App Admin = 'admin' or 'superadmin')
// — a completely separate permission from CA+'s own caPlusAdminEmails.
// This component renders nothing at all for anyone the backend doesn't
// authorize, rather than showing an error or empty state.
export default function BillingSettings({ user }) {
  const userId = user?.email || '';
  const [licensing, setLicensing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authorized, setAuthorized] = useState(false);

  const [showRequestForm, setShowRequestForm] = useState(false);
  const [requestInterest, setRequestInterest] = useState('');
  const [requestMessage, setRequestMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!userId) return;
    fetch(`${API_BASE}/api/billing?userId=${encodeURIComponent(userId)}`)
      .then(async (r) => {
        if (!r.ok) {
          setAuthorized(false);
          return null;
        }
        setAuthorized(true);
        return r.json();
      })
      .then((data) => { if (data) setLicensing(data.licensing); })
      .catch(() => setAuthorized(false))
      .finally(() => setLoading(false));
  }, [userId]);

  const submitRequest = async () => {
    if (!requestInterest) { alert('Please select what you\'re interested in'); return; }
    setSubmitting(true);
    try {
      const nameParts = (user?.name || '').trim().split(' ').filter(Boolean);
      // Guard against a single-word display name (e.g. just "Mario") —
      // the Lambda requires both first and last non-empty, matching the
      // marketing site's own form validation.
      const first = nameParts[0] || userId.split('@')[0] || 'Account';
      const last = nameParts.length > 1 ? nameParts.slice(1).join(' ') : 'Admin';
      // No org display name available in this component — fall back to
      // the email domain, still useful context for sales, and satisfies
      // the Lambda's required "company" field.
      const companyFallback = userId.includes('@') ? userId.split('@')[1] : '';
      const res = await fetch(LICENSE_REQUEST_LAMBDA, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          first,
          last,
          email: userId,
          company: companyFallback,
          interest: requestInterest,
          message: requestMessage || 'Requesting a licensing change for our AI Tools account.',
        }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        alert('Request sent — we\'ll follow up within 1 business day.');
        setShowRequestForm(false);
        setRequestInterest('');
        setRequestMessage('');
      } else {
        throw new Error(data.error || 'Unknown error');
      }
    } catch (err) {
      alert(err.message || 'Failed to send request — email hello@aecworks.ai directly.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading || !authorized) return null;

  return (
    <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '12px', padding: '24px', maxWidth: '700px', margin: '0 auto' }}>
      <h3 style={{ fontSize: '16px', fontWeight: '700', color: '#111827', marginBottom: '4px' }}>Billing & Licensing</h3>
      <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '20px' }}>
        Your firm's current AI Tools plan. Visible to App Admins only (set on ops.aecworks.ai).
      </p>

      {licensing ? (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
          <div>
            <div style={{ fontSize: '11px', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Plan</div>
            <div style={{ fontSize: '20px', fontWeight: '700', marginTop: '4px', textTransform: 'capitalize' }}>{licensing.tier}</div>
          </div>
          <div>
            <div style={{ fontSize: '11px', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Seats</div>
            <div style={{ fontSize: '20px', fontWeight: '700', marginTop: '4px' }}>{licensing.seats}</div>
          </div>
          <div>
            <div style={{ fontSize: '11px', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Concurrent User Limit</div>
            <div style={{ fontSize: '20px', fontWeight: '700', marginTop: '4px' }}>{licensing.concurrentUserLimit ?? 'Unlimited'}</div>
          </div>
          <div>
            <div style={{ fontSize: '11px', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Idle Timeout</div>
            <div style={{ fontSize: '20px', fontWeight: '700', marginTop: '4px' }}>
              {licensing.sessionTimeoutMinutes ? `${licensing.sessionTimeoutMinutes} min` : 'Never'}
            </div>
          </div>
        </div>
      ) : (
        <div style={{ fontSize: '13px', color: '#9ca3af', marginBottom: '20px' }}>No licensing info found for this account.</div>
      )}

      {!showRequestForm ? (
        <button
          style={{ background: '#D4AF37', color: '#fff', border: 'none', borderRadius: '6px', padding: '10px 16px', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}
          onClick={() => setShowRequestForm(true)}
        >
          Request a Licensing Change
        </button>
      ) : (
        <div style={{ border: '1px solid #e5e7eb', borderRadius: '8px', padding: '16px', backgroundColor: '#f9fafb' }}>
          <div style={{ fontSize: '13px', fontWeight: '600', marginBottom: '12px' }}>Request a Licensing Change</div>
          <div style={{ marginBottom: '12px' }}>
            <label style={{ fontSize: '12px', color: '#374151', display: 'block', marginBottom: '4px' }}>What are you interested in?</label>
            <select
              style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '13px' }}
              value={requestInterest}
              onChange={(e) => setRequestInterest(e.target.value)}
            >
              <option value="">Select an option…</option>
              <option>AI Tools — Team (10 seats min, $149.99/seat/mo)</option>
              <option>AI Tools — Studio (25–49 seats, $99.99/seat/mo)</option>
              <option>AI Tools — Enterprise (50+ seats, custom pricing)</option>
              <option>Other</option>
            </select>
          </div>
          <div style={{ marginBottom: '12px' }}>
            <label style={{ fontSize: '12px', color: '#374151', display: 'block', marginBottom: '4px' }}>Additional details (optional)</label>
            <textarea
              style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '13px' }}
              rows={4}
              value={requestMessage}
              onChange={(e) => setRequestMessage(e.target.value)}
              placeholder="e.g. how many additional seats, timeline, etc."
            />
          </div>
          <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
            <button
              style={{ background: 'none', border: '1px solid #d1d5db', borderRadius: '6px', padding: '8px 14px', fontSize: '13px', cursor: 'pointer' }}
              onClick={() => setShowRequestForm(false)}
            >
              Cancel
            </button>
            <button
              style={{ background: '#D4AF37', color: '#fff', border: 'none', borderRadius: '6px', padding: '8px 14px', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}
              onClick={submitRequest}
              disabled={submitting}
            >
              {submitting ? 'Sending...' : 'Send Request'}
            </button>
          </div>
          <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '10px' }}>We typically respond within 1 business day.</div>
        </div>
      )}
    </div>
  );
}